import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaCarSide, FaCar, FaShuttleVan, FaMotorcycle } from "react-icons/fa";
import Translate from "../../LanguageTranslation/Translate";
import axiosInstance from "../../../helpers/axiosInstance";

const BRAND = "#286328";
const BRAND_FILL = "rgba(40,99,40,0.10)";
const LABEL_W = "220px";
const FIELD_W = "240px";
const DD_PROPS = {
  variant: "ghost",
  fontWeight: "bold",
  px: 4,
  py: 2,
  border: "1px solid #d8d8d8",
  borderRadius: "md",
  bg: "#FFFBEF",
  _hover: { bg: "#F5F5F5" },
};

const vehicleOpts = [
  { key: "standard", label: "Standard Car", icon: FaCarSide },
  { key: "luxury", label: "Luxury", icon: FaCar },
  { key: "suv", label: "SUV", icon: FaCarSide },
  { key: "van", label: "Van", icon: FaShuttleVan },
  { key: "motorcycle", label: "Motorcycle", icon: FaMotorcycle },
];

export default function CarTripDetails({
  carVehicleType,
  setCarVehicleType,
  carDistance,
  setCarDistance,
  carFuelType,
  setCarFuelType,
  carFuelConsumed,
  setCarFuelConsumed,
  carResult,
  setCarResult,
  selectedTrip,
  hasTrips,
}) {
  const [loading, setLoading] = useState(false);

  const calculateCarEmissions = async () => {
    // Check if trip is selected / any trips exist
    if (!selectedTrip || selectedTrip.trim() === '') {
      alert(hasTrips
        ? 'Please select a trip before calculating emissions.'
        : 'No trips found. Please create a trip plan first, then select it before calculating emissions.'
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        vehicle_type: carVehicleType,
        distance: Number(carDistance),
        fuel_type: carFuelType,
        fuel_consumed: carFuelConsumed ? Number(carFuelConsumed) : undefined,
      };
      const { data } = await axiosInstance.post(
        "/co2-emissions/calculate/car",
        payload
      );
      setCarResult(data);
    } catch (err) {
      console.error("Error calculating car emissions:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Select Vehicle */}
      <Heading size="sm" mb={4}>
        <Translate text="Select Vehicle" />
      </Heading>
      <HStack spacing={14} mb={10} wrap="wrap">
        {vehicleOpts.map((opt) => {
          const active = carVehicleType === opt.key;
          return (
            <VStack
              key={opt.key}
              spacing={1}
              cursor="pointer"
              onClick={() => setCarVehicleType(opt.key)}
            >
              <Box
                bg={active ? BRAND_FILL : "#E9E7E1"}
                border={active ? `2px solid ${BRAND}` : "2px solid transparent"}
                boxSize="58px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  as={opt.icon}
                  boxSize={6}
                  color={active ? BRAND : "#666"}
                />
              </Box>
              <Text fontSize="sm" textAlign="center" whiteSpace="pre-line">
                <Translate text={opt.label} />
              </Text>
            </VStack>
          );
        })}
      </HStack>

      {/* Distance */}
      <Flex mb={6} align="center">
        <Box w={LABEL_W}>
          <Translate text="Distance (km)" />
        </Box>
        <Input
          placeholder="Enter distance in km"
          value={carDistance}
          onChange={(e) => setCarDistance(e.target.value)}
          bg="#FFFBEF"
          border="1px solid #d8d8d8"
          w={FIELD_W}
        />
      </Flex>

      {/* Fuel Type */}
      <Flex mb={6} align="center">
        <Box w={LABEL_W}>
          <Translate text="Fuel Type" />
        </Box>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w={FIELD_W}
            textAlign="left"
            justifyContent="space-between"
            {...DD_PROPS}
          >
            <Translate
              text={carFuelType.charAt(0).toUpperCase() + carFuelType.slice(1)}
            />
          </MenuButton>
          <MenuList>
            {["gasoline", "diesel", "electric"].map((f) => (
              <MenuItem key={f} onClick={() => setCarFuelType(f)}>
                <Translate text={f.charAt(0).toUpperCase() + f.slice(1)} />
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      {/* Fuel Consumed */}
      <Flex mb={6} align="center">
        <Box w={LABEL_W}>
          <Translate text="Fuel Consumed (L)" />
        </Box>
        <Input
          placeholder="Liters consumed"
          value={carFuelConsumed}
          onChange={(e) => setCarFuelConsumed(e.target.value)}
          bg="#FFFBEF"
          border="1px solid #d8d8d8"
          w={FIELD_W}
        />
      </Flex>

      <Divider mb={8} />

      {/* Calculate */}
      <Button
        w="full"
        bg={BRAND}
        color="white"
        _hover={{ bg: BRAND }}
        borderRadius="lg"
        py={6}
        fontWeight="bold"
        onClick={calculateCarEmissions}
        isLoading={loading}
      >
        <Translate text="Calculate" />
      </Button>
    </>
  );
}
